'use client';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';

interface StoreInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    storeName: string;
}

const StoreInfoModal = ({ isOpen, onClose, storeName }: StoreInfoModalProps) => {
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [isClosing, setIsClosing] = useState(false);

    // Функция плавного закрытия модального окна
    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 300); // Длительность анимации в мс
    };

    // Сбрасываем состояние при открытии модального окна
    useEffect(() => {
        if (isOpen) {
            setIsClosing(false);
        }
    }, [isOpen]);

    // Закрытие по клавише Escape
    useEffect(() => {
        const handleEscapeKey = (e: KeyboardEvent) => {
            if (isOpen && e.key === 'Escape') {
                handleClose();
            }
        };

        window.addEventListener('keydown', handleEscapeKey);
        return () => window.removeEventListener('keydown', handleEscapeKey);
    }, [isOpen]);

    // Данные о филиале
    const storeInfo = {
        name: storeName,
        address: 'г. Ташкент, Мирабадский район, ул. Фидокор, 10',
        workingHours: 'Пн–Пт: 10:00–21:00, Сб–Вс: 11:00–20:00',
        phone: '+998 99 666-00-90',
        description: 'Официальный магазин Vitaline с широким ассортиментом товаров для здоровья и красоты.',
        photos: [
            'https://retail.vitaline.uz/wp-content/uploads/2025/05/fidokor-3-min.jpg',
            'https://retail.vitaline.uz/wp-content/uploads/2025/05/fidokor-2-min.jpg',
            'https://retail.vitaline.uz/wp-content/uploads/2025/05/fidokor-1-min.jpg',
        ],
        yandexMapLink: 'https://yandex.uz/maps/-/CHrQZUld'
    };

    // Функции для переключения фотографий
    const nextPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev + 1) % storeInfo.photos.length);
    };

    const prevPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev - 1 + storeInfo.photos.length) % storeInfo.photos.length);
    };

    if (!isOpen) return null;

    // Варианты анимации для закрытия и открытия
    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.3 } },
        exit: { opacity: 0, transition: { duration: 0.3 } }
    };

    const modalVariants = {
        hidden: { scale: 0.95, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: {
                type: "spring",
                damping: 25,
                stiffness: 300
            }
        },
        exit: {
            scale: 0.95,
            opacity: 0,
            transition: {
                duration: 0.2
            }
        }
    };

    // Закрытие при клике на фон
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <motion.div
                    key="modal-backdrop"
                    variants={backdropVariants}
                    initial="hidden"
                    animate={isClosing ? "exit" : "visible"}
                    exit="exit"
                    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[99999999]"
                    onClick={handleBackdropClick}
                >
                    <motion.div
                        key="modal-content"
                        variants={modalVariants}
                        initial="hidden"
                        animate={isClosing ? "exit" : "visible"}
                        exit="exit"
                        className="bg-[white] rounded-2xl shadow-2xl max-w-5xl w-full max-h-[80vh] md:max-h-[90vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Контейнер для скроллабельного содержимого */}
                        <div className="overflow-auto max-h-[90vh] md:max-h-[90vh] pr-0 md:pr-3 scrollbar-hide">
                            {/* Шапка */}
                            <div className="p-6 border-b border-orange-100 flex justify-between items-center bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                                <h2 className="text-2xl font-bold text-gray-800 tracking-tight">{storeInfo.name}</h2>
                                <button
                                    onClick={handleClose}
                                    className="p-2 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 hover:text-orange-700 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Основное содержимое */}
                            <div className="pt-3 pb-[100px] md:pb-6 md:pt-6 p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Левая колонка: Информация и фотографии */}
                                <div className="space-y-6">
                                    {/* Информация */}
                                    <motion.div
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.1 }}
                                        className="space-y-4"
                                    >
                                        <p className="text-gray-700 leading-relaxed text-base font-medium mb-6">{storeInfo.description}</p>
                                        <div className="space-y-4">
                                            {/* Адрес */}
                                            <div className="flex items-start">
                                                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <h4 className="font-medium text-gray-900">Адрес</h4>
                                                    <p className="text-gray-600">{storeInfo.address}</p>
                                                </div>
                                            </div>

                                            {/* Время работы */}
                                            <div className="flex items-start">
                                                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <h4 className="font-medium text-gray-900">Время работы</h4>
                                                    <p className="text-gray-600">{storeInfo.workingHours}</p>
                                                </div>
                                            </div>

                                            {/* Телефон */}
                                            <div className="flex items-start">
                                                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
                                                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <h4 className="font-medium text-gray-900">Телефон</h4>
                                                    <p className="text-gray-600">
                                                        <a href={`tel:${storeInfo.phone}`} className="">
                                                            {storeInfo.phone}
                                                        </a>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Карусель фотографий */}
                                    <motion.div
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.2 }}
                                        className="relative"
                                    >
                                        <div className="h-[300px] rounded-xl overflow-hidden bg-gray-100 relative shadow-md">
                                            <AnimatePresence mode="wait">
                                                <motion.div
                                                    key={currentPhotoIndex}
                                                    initial={{ opacity: 0, x: 50 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{ opacity: 0, x: -50 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="absolute inset-0"
                                                >
                                                    <Image
                                                        src={storeInfo.photos[currentPhotoIndex]}
                                                        alt={`Фото магазина ${currentPhotoIndex + 1}`}
                                                        layout="fill"
                                                        objectFit="cover"
                                                        className="rounded-xl"
                                                    />
                                                </motion.div>
                                            </AnimatePresence>
                                        </div>
                                        {storeInfo.photos.length > 1 && (
                                            <>
                                                <button
                                                    onClick={prevPhoto}
                                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-orange-500 text-white p-3 rounded-full hover:bg-orange-600 transition"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={nextPhoto}
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-orange-500 text-white p-3 rounded-full hover:bg-orange-600 transition"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2">
                                                    {storeInfo.photos.map((_, index) => (
                                                        <motion.span
                                                            key={index}
                                                            className={`h-2 w-2 rounded-full ${index === currentPhotoIndex ? 'bg-orange-500' : 'bg-gray-300'}`}
                                                            animate={{ scale: index === currentPhotoIndex ? 1.2 : 1 }}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </motion.div>
                                </div>

                                {/* Правая колонка: Карта */}
                                <motion.div
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.3 }}
                                    className="flex flex-col h-full"
                                >
                                    <div className="flex-grow rounded-xl overflow-hidden relative shadow-md mb-4">
                                        <iframe
                                            src="https://yandex.ru/map-widget/v1/?um=constructor%3Aae02b8e6909a4ac1b7e37ec17aec195024d8dcaa60feaaf92981401a542b5b0e&source=constructor"
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            title="Yandex Map"
                                            className="rounded-xl"
                                        ></iframe>
                                    </div>
                                    <a
                                        href={storeInfo.yandexMapLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group flex items-center justify-center px-5 py-3 bg-[#ffedd5] border border-gray-200 hover:bg-gray-50 text-[#c2410c] font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                        </svg>
                                        <span>Открыть в Яндекс Картах</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 text-[#c2410c]" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </a>
                                </motion.div>
                            </div>

                            {/* Футер */}
                            <div className="p-1 md:p-1 border-t border-orange-100 bg-white/80 backdrop-blur-sm">
                                {/* Футер без кнопки закрытия, как в вашем последнем коде */}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default StoreInfoModal;